import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2, Loader2, ShieldCheck, Info } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { ClientPushButton } from "../components/ClientPushButton";
import { api, SERVICES } from "../lib/api";
import { formatApiError } from "../context/AuthContext";
import { QRCodeCanvas } from "qrcode.react";

export default function RequestPage() {
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", service_type: "Stampa 3D", title: "", description: "", material: "", quantity: 1, expected_delivery: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [discountCode, setDiscountCode] = useState("");
  const [privacyText, setPrivacyText] = useState("");
  const [orderCode, setOrderCode] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setPrivacyText(r.data.texts?.privacy_text || "")).catch(() => {});
    const c = new URLSearchParams(window.location.search).get("code");
    if (c) setDiscountCode(c.toUpperCase());
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.title || !form.description) {
      toast.error("Compila nome, titolo e descrizione.");
      return;
    }
    if (!consent) {
      toast.error("Devi accettare l'informativa sulla privacy per inviare.");
      return;
    }
    setSubmitting(true);
    try {
      let attachment_path = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_path = up.data.path;
      }
      const res = await api.post("/requests", { ...form, quantity: Number(form.quantity) || 1, attachment_path, privacy_consent: consent, email_opt_in: emailOptIn, discount_code: discountCode.trim().toUpperCase() || null });
      setOrderCode(res.data.order_code || "");
      setDone(true);
      toast.success("Richiesta inviata con successo!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore nell'invio.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div className="noise-overlay min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 py-28 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
            <CheckCircle2 className="w-16 h-16 text-neon-green mx-auto drop-shadow-[0_0_16px_rgba(57,255,20,0.6)]" />
            <h1 className="font-display font-black text-3xl tracking-tighter text-white mt-6">Richiesta ricevuta!</h1>
            <p className="text-muted-foreground font-light mt-3">Grazie {form.client_name}. Ti ricontatteremo al più presto per il tuo progetto.</p>
            {orderCode && (
              <div data-testid="order-code-box" className="mt-6 inline-block px-6 py-4 bg-card border border-neon-cyan/40 rounded-sm animate-glow-breathe">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Il tuo codice ordine</p>
                <p className="font-display font-black text-3xl text-neon-cyan mt-1 drop-shadow-[0_0_14px_rgba(0,240,255,0.5)]">{orderCode}</p>
                <p className="text-xs text-muted-foreground font-light mt-2">Conservalo: puoi controllare lo stato nella pagina <b>Stato</b>.</p>
              </div>
            )}
            <p data-testid="cancel-policy-success" className="text-xs text-muted-foreground font-light mt-4 max-w-md mx-auto leading-relaxed">
              Puoi annullare l'ordine dalla pagina <b>Stato</b> finché è <b className="text-white">"In attesa"</b>; una volta <b className="text-white">"In lavorazione"</b> non sarà più annullabile.
            </p>
            <div>
              <ClientPushButton email={form.client_email} orderCode={orderCode} />
            </div>
            <br />
            <Button data-testid="new-request-btn" onClick={() => { setDone(false); setOrderCode(""); setForm({ client_name: "", client_email: "", client_phone: "", service_type: "Stampa 3D", title: "", description: "", material: "", quantity: 1, expected_delivery: "" }); setFile(null); setConsent(false); setEmailOptIn(true); setDiscountCode(""); }}
              className="mt-8 bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold transition-colors duration-200">
              Invia un'altra richiesta
            </Button>
          </motion.div>
        </div>
      </div>
    );

  return (
    <div className="noise-overlay min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-14">
        <span className="font-mono text-xs uppercase tracking-widest text-neon-cyan">Nuovo progetto</span>
        <h1 className="font-display font-black text-4xl tracking-tighter text-white mt-2 mb-8">Richiesta di stampa</h1>

        <form onSubmit={submit} className="space-y-6 bg-card border border-white/10 rounded-sm p-6 sm:p-8">
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Servizio</Label>
            <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
              <SelectTrigger data-testid="req-service" className="fld"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-white/10 text-white">
                {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Nome *"><Input data-testid="req-name" value={form.client_name} onChange={set("client_name")} placeholder="Il tuo nome" className="fld" /></Field>
            <Field label="Email"><Input data-testid="req-email" type="email" value={form.client_email} onChange={set("client_email")} placeholder="email@esempio.it" className="fld" /></Field>
            <Field label="Telefono"><Input data-testid="req-phone" value={form.client_phone} onChange={set("client_phone")} placeholder="+39 ..." className="fld" /></Field>
            <Field label="Quantità"><Input data-testid="req-quantity" type="number" min="1" value={form.quantity} onChange={set("quantity")} className="fld" /></Field>
            <Field label="Codice buono sconto (facoltativo)"><Input data-testid="req-discount-code" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} placeholder="Es. FEDELE-XXXXX" className="fld" /></Field>
          </div>
          <Field label="Titolo del progetto *"><Input data-testid="req-title" value={form.title} onChange={set("title")} placeholder="Es. Supporto per cuffie" className="fld" /></Field>
          <Field label="Materiale / colore"><Input data-testid="req-material" value={form.material} onChange={set("material")} placeholder="Es. PLA nero, PETG trasparente" className="fld" /></Field>
          <Field label="Descrizione *"><Textarea data-testid="req-description" value={form.description} onChange={set("description")} rows={5} placeholder="Descrivi dimensioni, dettagli, tempistiche..." className="fld resize-none" /></Field>
          <Field label="Data presunta di consegna (facoltativa)"><Input data-testid="req-expected-delivery" type="date" value={form.expected_delivery} onChange={set("expected_delivery")} className="fld" /></Field>

          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">Immagine / file di riferimento</Label>
            <label data-testid="req-file-label" className="flex items-center gap-3 cursor-pointer border border-dashed border-white/20 hover:border-neon-cyan/60 rounded-sm px-4 py-4 transition-colors duration-200">
              <UploadCloud className="w-5 h-5 text-neon-cyan" />
              <span className="text-sm text-muted-foreground truncate">{file ? file.name : "Clicca per caricare un'immagine o file (.stl, .png, .jpg)"}</span>
              <input data-testid="req-file-input" type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} accept="image/*,.stl,.obj,.pdf" />
            </label>
          </div>

          <div data-testid="privacy-box" className="flex gap-3 items-start p-4 bg-input/50 border border-white/10 rounded-sm">
            <Checkbox data-testid="req-consent" id="req-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-neon-cyan data-[state=checked]:bg-neon-cyan data-[state=checked]:text-black" />
            <label htmlFor="req-consent" className="text-xs text-muted-foreground font-light leading-relaxed cursor-pointer">
              <span className="flex items-center gap-1.5 text-neon-cyan font-mono uppercase tracking-wider text-[11px] mb-1"><ShieldCheck className="w-3.5 h-3.5" /> Consenso privacy (obbligatorio)</span>
              {privacyText || "Acconsento al trattamento dei miei dati personali ai sensi del Reg. (UE) 2016/679 (GDPR) e del D.Lgs. 196/2003."}
            </label>
          </div>

          <div data-testid="email-optin-box" className="flex gap-3 items-start px-1">
            <Checkbox data-testid="req-email-optin" id="req-email-optin" checked={emailOptIn} onCheckedChange={(v) => setEmailOptIn(!!v)} className="mt-0.5 border-neon-green data-[state=checked]:bg-neon-green data-[state=checked]:text-black" />
            <label htmlFor="req-email-optin" className="text-xs text-muted-foreground font-light leading-relaxed cursor-pointer">
              Desidero ricevere <b className="text-neon-green">aggiornamenti via email</b> sullo stato del mio ordine. Potrai disiscriverti in qualsiasi momento dal link presente nelle email (richiede l'inserimento dell'email).
            </label>
          </div>

          <div data-testid="cancel-policy-note" className="flex gap-2.5 items-start p-3.5 rounded-sm border border-neon-cyan/30 bg-neon-cyan/5">
            <Info className="w-4 h-4 text-neon-cyan shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground font-light leading-relaxed">
              Potrai <b className="text-white">annullare l'ordine</b> dalla pagina <b>Stato</b> (usando il tuo codice ordine) finché lo stato è <b className="text-white">"In attesa"</b>. Una volta che l'ordine passa <b className="text-white">"In lavorazione"</b> non sarà più possibile annullarlo.
            </p>
          </div>

          <Button data-testid="req-submit-btn" type="submit" disabled={submitting || !consent}
            className="w-full h-12 bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold text-base gap-2 transition-colors duration-200 disabled:opacity-60">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Invio in corso…</> : "Invia richiesta"}
          </Button>
        </form>
      </div>
      <style>{`.fld{background:hsl(var(--input));border-color:hsl(var(--border));color:white}.fld:focus-visible{outline:none;box-shadow:0 0 0 2px #00F0FF}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm text-muted-foreground mb-2 block">{label}</Label>
    {children}
  </div>
);
